import React from "react";
import PreviewFieldColumn, { type FieldEntry } from "./PreviewFieldColumn";

export type { FieldEntry };

export type GatepassDetailsSectionGroup = {
  subtitle?: string;
  rightSubtitle?: string;
  leftFields?: FieldEntry[];
  // Side-by-side with leftFields (Job Details | Vehicle Details). Omit for full-width rows.
  rightFields?: FieldEntry[];
  // Items shown in a responsive two-column grid (e.g. truck cargo types).
  gridItems?: Array<{ title: string; fields: FieldEntry[] }>;
};

export type GatepassDetailsSectionProps = {
  testIdBase: string;
  title: string;
  sections: GatepassDetailsSectionGroup[];
};

const isTwoColumnSection = (
  section: GatepassDetailsSectionGroup,
): section is GatepassDetailsSectionGroup & {
  rightFields: FieldEntry[];
} => section.rightFields !== undefined && section.gridItems === undefined;

const GatepassDetailsSection: React.FC<GatepassDetailsSectionProps> = ({
  testIdBase,
  title,
  sections,
}) => {
  return (
    <div className="mb-8 no-border" data-testid={`${testIdBase}-details-section`}>
      <div className="border border-gray-200 rounded-md bg-white">
        {sections.map((section, index) => {
          const sectionTitle = index === 0 ? title : section.subtitle;
          const sectionRightTitle = index === 0 ? undefined : section.rightSubtitle;

          return (
            <div
              key={`${section.subtitle ?? "section"}-${index}`}
              className={`${index > 0 ? "border-t border-gray-200" : ""} p-4`}
            >
              {section.gridItems ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {section.gridItems.map((item, itemIndex) => (
                    <div key={`${item.title}-${itemIndex}`}>
                      <div className="mb-4 font-semibold">{item.title}</div>
                      <PreviewFieldColumn fields={item.fields} />
                    </div>
                  ))}
                </div>
              ) : isTwoColumnSection(section) ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-4">
                    <div className="font-semibold">{sectionTitle ?? ""}</div>
                    <div className="font-semibold">{sectionRightTitle ?? ""}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <PreviewFieldColumn fields={section.leftFields ?? []} />
                    <PreviewFieldColumn fields={section.rightFields} />
                  </div>
                </>
              ) : (
                <>
                  {sectionTitle && <div className="mb-4 font-semibold">{sectionTitle}</div>}
                  <PreviewFieldColumn fields={section.leftFields ?? []} />
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GatepassDetailsSection;
